/**
 * CameraCaptureDialog
 * ----------------------------------------------------------------------------
 * Captura de foto pela câmera do aparelho DENTRO do sistema (getUserMedia).
 * Usado nos itens de vistoria "Foto — Somente câmera": não há acesso à galeria,
 * a imagem é gerada no momento da captura (canvas → JPEG).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-14 v1.4.0 - Prop opcional `maxSide` (redução da foto antes do envio).
 *   RELEASE: 2026-10-13 v1.3.0 - Prop opcional `watermark` (linhas gravadas no rodapé da foto) e
 *            `title`/`description`; sem prop o comportamento da Vistoria é idêntico.
 *   RELEASE: 2026-09-27 v1.2.0 - Prop opcional `facing` ('user' para selfie do aceite);
 *            padrão continua câmera traseira.
 *   RELEASE: 2026-09-26 v1.1.0 - Adota o cabeçalho e rodapé do modal de
 *            anotações e aguarda o salvamento da foto antes de fechar.
 *   RELEASE: 2026-09-26 v1.0.0 - Versão inicial.
 */
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, RotateCcw, X } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Recebe o arquivo JPEG capturado */
  onCapture: (file: File) => Promise<boolean | void> | boolean | void;
  /** Câmera preferida: 'environment' (traseira, padrão) ou 'user' (frontal/selfie) */
  facing?: 'environment' | 'user';
  /** Linhas da marca d'água gravadas na imagem (lidas no momento da captura). */
  watermark?: () => string[];
  title?: string;
  description?: string;
  /** Maior lado da foto em px (reduz proporcionalmente); sem prop mantém a resolução da câmera. */
  maxSide?: number;
}

export function CameraCaptureDialog({ open, onOpenChange, onCapture, facing = 'environment', watermark, title = 'Tirar foto', description = 'Capture a evidência e confira a imagem antes de salvá-la na vistoria.', maxSide }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Liga/desliga a câmera conforme o modal abre/fecha
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      setError(null);
      setStarting(true);
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('Este aparelho/navegador não permite acesso à câmera.');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch (e: any) {
        setError(e?.name === 'NotAllowedError'
          ? 'Permissão da câmera negada. Libere o acesso nas configurações do navegador.'
          : e?.message || 'Nenhuma câmera disponível.');
      } finally {
        setStarting(false);
      }
    };
    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setPreview((p) => { if (p) URL.revokeObjectURL(p.url); return null; });
    };
  }, [open]);

  // Congela o quadro atual do vídeo em JPEG
  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    // v1.4.0: reduz proporcionalmente quando `maxSide` é informado (envio mais rápido)
    const scale = maxSide ? Math.min(1, maxSide / Math.max(v.videoWidth, v.videoHeight)) : 1;
    c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
    const ctx = c.getContext('2d');
    ctx?.drawImage(v, 0, 0, c.width, c.height);
    // Marca d'água: faixa escura semitransparente no rodapé, texto branco legível em qualquer fundo.
    const lines = watermark?.() ?? [];
    if (ctx && lines.length) {
      const size = Math.max(14, Math.round(c.width / 45));
      const pad = Math.round(size * 0.6);
      const height = lines.length * size * 1.35 + pad * 2;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, c.height - height, c.width, height);
      ctx.fillStyle = '#ffffff';
      ctx.font = `600 ${size}px sans-serif`;
      ctx.textBaseline = 'top';
      lines.forEach((line, i) => ctx.fillText(line, pad, c.height - height + pad + i * size * 1.35, c.width - pad * 2));
    }
    c.toBlob((b) => { if (b) setPreview({ url: URL.createObjectURL(b), blob: b }); }, 'image/jpeg', 0.9);
  };

  /** Confirma e só fecha quando o item tiver sido salvo com sucesso. */
  const confirm = async () => {
    if (!preview) return;
    const name = `camera-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
    setConfirming(true);
    try {
      const saved = await onCapture(new File([preview.blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
      if (saved !== false) onOpenChange(false);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        {/* Cabeçalho premium compartilhando a linguagem visual das anotações. */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Camera className="h-3.5 w-3.5 text-primary" />
              </span>
              {title}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5">
          {error ? (
            <p className="border-l-2 border-destructive pl-3 text-sm text-destructive">{error}</p>
          ) : (
            <div className="relative rounded-md overflow-hidden bg-muted aspect-video border">
              {starting && <Loader2 className="absolute inset-0 m-auto h-6 w-6 animate-spin" />}
              <video ref={videoRef} playsInline muted className={preview ? 'hidden' : 'w-full h-full object-contain'} />
              {preview && <img src={preview.url} alt="Foto capturada" className="w-full h-full object-contain" />}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button variant="outline" size="sm" className="h-7 px-3" disabled={confirming} onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />Cancelar
          </Button>
          {!error && (preview ? (
            <>
              <Button variant="outline" size="sm" className="h-7 px-3" disabled={confirming}
                onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
                <RotateCcw className="h-4 w-4" />Refazer
              </Button>
              <Button size="sm" className="h-7 px-3" onClick={confirm} disabled={confirming}>
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                {confirming ? 'Salvando...' : 'Usar foto'}
              </Button>
            </>
          ) : (
            <Button size="sm" className="h-7 px-3" onClick={capture} disabled={starting || confirming}>
              <Camera className="h-4 w-4" />Capturar
            </Button>
          ))}
          </div>
      </DialogContent>
    </Dialog>
  );
}
